import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/auth/AuthContext";
import { useAuth } from "@/auth/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { PageHeader, Card, Button } from "@/components/ui-kit";
import { toast } from "sonner";
import { PaperPlaneTilt, Brain, ArrowsClockwise, Paperclip, X, Sparkle } from "@phosphor-icons/react";

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const Mentor = () => {
  const { t, lang } = useT();
  const { user } = useAuth();
  const [sessionId] = useState(() => localStorage.getItem("mentor_session") || (() => { const id = crypto.randomUUID(); localStorage.setItem("mentor_session", id); return id; })());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [attached, setAttached] = useState(null); // {name, preview (data url)}
  const endRef = useRef();
  const fileRef = useRef();
  const isPremium = user?.plan && user.plan !== "FREE";

  useEffect(() => {
    api.get(`/mentor/history/${sessionId}`).then(r => setMessages(r.data)).catch(() => {});
  }, [sessionId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const onFile = async (f) => {
    if (!f) return;
    if (!isPremium) {
      toast.error(lang === "cs" ? "Nahrávání obrázků je pro PREMIUM/ELITE plán" : "Image upload is a PREMIUM/ELITE feature");
      return;
    }
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) {
      toast.error(lang === "cs" ? "Jen PNG, JPG nebo WEBP" : "PNG, JPG or WEBP only");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Max 10MB");
      return;
    }
    const preview = await fileToBase64(f);
    setAttached({ name: f.name, preview });
  };

  const removeAttached = () => {
    setAttached(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const send = async (e) => {
    e?.preventDefault();
    if ((!input.trim() && !attached) || sending) return;
    const msg = input.trim() || (lang === "cs" ? "Zanalyzuj tento graf." : "Analyze this chart.");
    const hadImage = !!attached;
    setMessages([...messages, { role: "user", content: msg, has_image: hadImage, id: "tmp-" + Date.now() }]);
    setInput("");
    const payload = { session_id: sessionId, message: msg };
    if (attached) payload.image_base64 = attached.preview;
    removeAttached();
    setSending(true);
    try {
      const { data } = await api.post("/mentor/chat", payload);
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply, id: "r-" + Date.now() }]);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setMessages((prev) => [...prev, { role: "assistant", content: "Error: " + detail, id: "e-" + Date.now() }]);
      if (err.response?.status === 402) toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const suggestions = lang === "cs" ? [
    "Proč jsem dostal stop loss?",
    "Jak dodržet pravidla prop firm challenge?",
    "Vysvětli mi Market Structure",
    "Jak zvládnout FOMO?",
  ] : [
    "Why did I get stopped out?",
    "How to respect prop firm challenge rules?",
    "Explain Market Structure",
    "How to manage FOMO?",
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] lg:h-screen">
      <PageHeader title={t("mentor.title")} sub={t("mentor.sub")} />
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-4" data-testid="mentor-chat">
        {messages.length === 0 && (
          <div className="max-w-2xl mx-auto">
            <Card className="p-6 text-center">
              <Brain weight="thin" size={48} className="mx-auto text-blue-400 mb-3" />
              <p className="text-sm text-zinc-300">{t("mentor.greeting")}</p>
              {isPremium && (
                <p className="text-xs text-blue-300 mt-3 inline-flex items-center gap-1.5 bg-blue-500/10 border border-blue-500/30 rounded-md px-3 py-1.5">
                  <Sparkle weight="fill" size={12} />
                  {lang === "cs" ? "Můžeš mi poslat i screenshot grafu — kliknu na spony a nahraj obrázek." : "You can send me chart screenshots — click the paperclip and upload."}
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-6">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => setInput(s)} data-testid={`mentor-suggestion-${i}`} className="text-xs text-zinc-300 hover:text-zinc-100 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded-md p-3 text-left transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </Card>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-2xl rounded-lg px-4 py-3 ${m.role === "user" ? "bg-blue-600 text-white" : "bg-zinc-900 border border-zinc-800 text-zinc-200"}`}>
              {m.has_image && (
                <div className={`text-xs mb-1.5 flex items-center gap-1 ${m.role === "user" ? "text-blue-100" : "text-zinc-400"}`}>
                  <Paperclip size={12} weight="bold" /> {lang === "cs" ? "s obrázkem" : "with image"}
                </div>
              )}
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.content}</div>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 text-zinc-400 inline-flex items-center gap-2 text-sm">
              <ArrowsClockwise size={14} className="animate-spin" /> {lang === "cs" ? "Mentor přemýšlí..." : "Mentor is thinking..."}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="border-t border-zinc-800 p-4 bg-zinc-950">
        <div className="max-w-4xl mx-auto">
          {attached && (
            <div className="mb-2 flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-md p-2" data-testid="mentor-attached">
              <img src={attached.preview} alt="preview" className="w-14 h-14 object-cover rounded" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono text-zinc-400 truncate">{attached.name}</div>
                <div className="text-xs text-blue-400">{lang === "cs" ? "Připojeno k dalšímu dotazu" : "Attached to next message"}</div>
              </div>
              <button type="button" onClick={removeAttached} data-testid="mentor-remove-attached" className="text-zinc-500 hover:text-red-400 p-1"><X weight="bold" size={16} /></button>
            </div>
          )}
          <div className="flex gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
              data-testid="mentor-file-input"
            />
            {isPremium ? (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="mentor-attach-btn"
                title={lang === "cs" ? "Připojit graf" : "Attach chart"}
                className="p-2.5 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-400 hover:text-blue-400 transition-colors"
              >
                <Paperclip weight="bold" size={18} />
              </button>
            ) : (
              <Link
                to="/app/upgrade"
                data-testid="mentor-attach-upgrade"
                title={lang === "cs" ? "PREMIUM funkce — nahrávání obrázků" : "PREMIUM feature — image upload"}
                className="p-2.5 rounded-md bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 text-blue-400 transition-colors inline-flex"
              >
                <Paperclip weight="bold" size={18} />
              </Link>
            )}
            <input
              data-testid="mentor-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("mentor.placeholder")}
              className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md px-4 py-2.5 text-zinc-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none placeholder-zinc-600"
            />
            <Button type="submit" data-testid="mentor-send" disabled={sending || (!input.trim() && !attached)} className="flex items-center gap-2 disabled:opacity-50">
              <PaperPlaneTilt weight="fill" size={14} /> {t("mentor.send")}
            </Button>
          </div>
          {!isPremium && (
            <p className="text-xs text-zinc-500 mt-2 text-center">
              {lang === "cs" ? "Chceš mentorovi poslat obrázek grafu? " : "Want to send the mentor a chart image? "}
              <Link to="/app/upgrade" className="text-blue-400 hover:text-blue-300 underline">
                {lang === "cs" ? "Upgrade na PREMIUM" : "Upgrade to PREMIUM"}
              </Link>
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default Mentor;
